import { useEffect, useRef, useState } from 'react';
import {
  ApiError, documentPageUrl, fetchProgramMatrix, type ProgramMatrix,
} from '../api.ts';

/**
 * The lender's own matrix, as the lender printed it.
 *
 * Henry, 2026-09-14: "All results should have availability to view the matrix that i
 * uploaded pertaining to the particular program/lender. Right now it just shows a snippet
 * that AI created."
 *
 * The snippet is MatrixPanel: our extraction of the one sub-table a citation sits in. It
 * is the more useful object for checking a single number and the less trusted one for
 * exactly the reason Henry names - it is ours. A broker has seen this lender's matrix
 * before, as a PDF, and the PDF is the thing they can hold our answer against. So this
 * viewer draws no table and adds no highlight: it is the page image and nothing else.
 *
 * WHICH PAGES. A matrix document is sometimes one tab of a combined file whose other
 * pages are guideline prose, and a lender's matrix PDF usually carries several programs.
 * The viewer opens on the first page carrying this program's cells, marks the pages that
 * are this program's, and lets the broker flip through the whole file anyway - the page
 * after the grid is often the footnotes, and hiding it would be editing the lender's
 * document for them.
 *
 * WHICH PROGRAM is decided by the card that opened this (see `quotedPrograms` in
 * Results.tsx). When that is more than one, they are offered side by side rather than
 * one picked: a finding's channel is a sentence the model wrote, and choosing between two
 * programs it matched equally well would be inventing a fact.
 *
 * Presented the way CitationViewer is - same overlay, same card, Escape and a click
 * outside both close - so the two panels a result card opens behave as one product.
 */

export interface QuotedProgram { programId: string; programName: string }

interface Props {
  lenderName: string;
  /** Most likely first. Never empty: the button that opens this renders nothing then. */
  programs: QuotedProgram[];
  onClose: () => void;
}

/** Keyed by the program it belongs to, so a tab switch never draws the last one's pages. */
type Load =
  | { programId: string; state: 'loading' }
  | { programId: string; state: 'ready'; matrix: ProgramMatrix }
  | { programId: string; state: 'failed'; message: string };

/** A chip that is the current choice: the program tab showing, the page being read. */
const PRESSED = { background: 'var(--blue)', borderColor: 'var(--blue)', color: '#fff' } as const;

/** Slimmer than .btn's 22px sides, so Previous, the page count and Next share a phone row. */
const PAGER_BUTTON = { padding: '10px 14px' } as const;

const NOTHING_HELD ="We don't hold a matrix page for this program.";

/** Said the way App.tsx's friendlyError says the same codes, for the same reader. */
function failureMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return NOTHING_HELD;
    if (e.status === 401) return 'Your session has expired. Sign in again to view the matrix.';
    if (e.status === 403) return 'This account does not have access to that.';
    if (e.status === 503) return "The lender's document isn't available right now.";
  }
  return "The matrix couldn't be loaded. Close this and try again.";
}

export default function ProgramMatrixViewer({ lenderName, programs, onClose }: Props) {
  const [programIdx, setProgramIdx] = useState(0);
  const program = programs[programIdx] ?? programs[0]!;
  const { programId } = program;

  const [load, setLoad] = useState<Load>({ programId, state: 'loading' });
  // Matrices already fetched while this panel is open, so flicking between two program
  // tabs is not a round trip each way. Not module-level like CitationViewer's slice
  // cache: a citation's cells are immutable, and a program's ACTIVE documents are not -
  // an ingest supersedes a version, and the next open should see it.
  const held = useRef(new Map<string, ProgramMatrix>());

  useEffect(() => {
    const cached = held.current.get(programId);
    if (cached) { setLoad({ programId, state: 'ready', matrix: cached }); return; }
    setLoad({ programId, state: 'loading' });
    const ac = new AbortController();
    let live = true;
    fetchProgramMatrix(programId, ac.signal)
      .then((matrix) => {
        if (!live) return;
        held.current.set(programId, matrix);
        setLoad({ programId, state: 'ready', matrix });
      })
      .catch((e: unknown) => {
        if (live) setLoad({ programId, state: 'failed', message: failureMessage(e) });
      });
    return () => { live = false; ac.abort(); };
  }, [programId]);

  // State from the previous program reads as loading until its own effect lands.
  const current: Load = load.programId === programId ? load : { programId, state: 'loading' };
  const matrix = current.state === 'ready' ? current.matrix : null;

  /*
    The document and page choices are stored WITH the key they were made under and read
    back only while that key still holds. Resetting them in an effect instead leaves one
    render in which a new program is drawn at the old program's page number - and two
    programs can share one combined PDF, so that page would load, look plausible, and be
    the wrong grid.
  */
  const [docChoice, setDocChoice] = useState({ programId, idx: 0 });
  const docIdx = docChoice.programId === programId ? docChoice.idx : 0;
  const doc = matrix?.documents[docIdx] ?? matrix?.documents[0] ?? null;

  const viewKey = doc ? `${programId}:${doc.versionId}` : '';
  const [pageChoice, setPageChoice] = useState({ key: '', page: 1 });
  const [zoom, setZoom] = useState(false);
  const lastPage = Math.max(1, doc?.pageCount ?? 1);
  const page = pageChoice.key === viewKey ? pageChoice.page : (doc?.pages[0] ?? 1);
  const goTo = (n: number): void => {
    setPageChoice({ key: viewKey, page: Math.min(lastPage, Math.max(1, n)) });
    setZoom(false);
  };

  const src = doc ? documentPageUrl(doc.versionId, page) : null;
  // Which src loaded and which failed, rather than one status reset on every flip: an
  // onLoad from the page being left cannot then mark the page being opened as ready.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const image = src === null ? 'none'
    : failedSrc === src ? 'failed'
    : loadedSrc === src ? 'ready'
    : 'loading';
  const isProgramPage = doc?.pages.includes(page) ?? false;

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // A new page starts at its top. The box keeps its scroll position across a src change,
  // so without this the second page opens wherever the first one was left - usually
  // halfway down a table, with nothing saying so.
  useEffect(() => {
    if (scrollRef.current) { scrollRef.current.scrollTop = 0; scrollRef.current.scrollLeft = 0; }
  }, [src]);

  // Escape closes and focus moves into the dialog, as in CitationViewer - but once, on
  // open. The parent's onClose is read through a ref so a re-render of the results page
  // does not re-run this and yank focus back off whatever button the broker is on. And
  // focus goes back to "View Matrix" on close, so a keyboard user is not dropped at the
  // top of a two-thousand-pixel page.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  const shownLender = matrix?.lenderName ?? lenderName;
  const shownProgram = matrix?.programName ?? program.programName;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Narrower at phone width: 20px a side of a 400px screen is a tenth of the page
        // the matrix is trying to fit into.
        padding: 'clamp(8px, 3vw, 20px)',
        zIndex: 50,
      }}
    >
      <div
        ref={dialogRef}
        className="card"
        role="dialog"
        aria-modal="true"
        aria-label={`${shownProgram} matrix from ${shownLender}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          // As wide as CitationViewer gets with a grid in it. A rate matrix is usually a
          // landscape page, and every pixel narrower is a smaller figure in each cell.
          maxWidth: 1040, width: '100%', maxHeight: '90vh',
          // On a short screen - a phone in landscape, an iPhone SE with the toolbar showing -
          // the header, tabs and page chips can outgrow 90vh on their own; the dialog then
          // scrolls rather than pushing Next and Close out of reach.
          overflowY: 'auto',
          padding: 'clamp(12px, 3vw, 20px)',
          display: 'flex', flexDirection: 'column', gap: 12, outline: 'none',
        }}
      >
        <div>
          <div className="lbl">{shownLender} · rate matrix</div>
          <div className="serif" style={{ fontSize: 18, marginBottom: 4 }}>{shownProgram}</div>
          {doc ? (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>{doc.title}</div>
          ) : null}
        </div>

        {programs.length > 1 ? (
          <div role="group" aria-label="Program"
               style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {programs.map((p, i) => (
              <button key={p.programId} type="button" className="chip"
                      aria-pressed={i === programIdx}
                      onClick={() => {
                        // A tab opens its program at the start, every time - the same
                        // place "View Matrix" opened it - rather than wherever it was left.
                        setProgramIdx(i);
                        setDocChoice({ programId: p.programId, idx: 0 });
                        setPageChoice({ key: '', page: 1 });
                        setZoom(false);
                      }}
                      style={i === programIdx ? PRESSED : undefined}>
                {p.programName}
              </button>
            ))}
          </div>
        ) : null}

        {matrix && matrix.documents.length > 1 ? (
          <label style={{ display: 'block' }}>
            <span className="lbl">Document</span>
            <select className="fselect" value={docIdx}
                    onChange={(e) => {
                      setDocChoice({ programId, idx: Number(e.target.value) });
                      setZoom(false);
                    }}>
              {matrix.documents.map((d, i) => (
                <option key={d.versionId} value={i}>{d.title}</option>
              ))}
            </select>
          </label>
        ) : null}

        {/*
          Where this program sits in the file, when the file is more than this program.
          A combined PDF can put the grid on page 31 of 40, and Previous/Next alone would
          be thirty clicks back to it after a look at the footnotes.
        */}
        {doc && doc.pages.length > 0 && doc.pages.length < lastPage ? (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            This program&apos;s grid is on page{doc.pages.length === 1 ? '' : 's'}{' '}
            {doc.pages.map((p) => (
              <button key={p} type="button" className="chip" onClick={() => goTo(p)}
                      aria-label={`Go to page ${p}`}
                      aria-current={p === page ? 'page' : undefined}
                      style={p === page ? PRESSED : undefined}>
                {p}
              </button>
            ))}
          </div>
        ) : null}

        <div ref={scrollRef} style={{
          // Takes what the dialog has left and scrolls inside it; the floor is small so the
          // controls below always fit, rather than a tall floor that shoves them off-screen.
          flex: '1 1 auto', minHeight: 'min(160px, 30vh)', overflow: 'auto', background: '#f1f5f9',
          borderRadius: 6, border: '1px solid var(--hair, #e2e8f0)',
        }}>
          {current.state === 'loading' ? (
            <div style={{ padding: '28px 20px', fontSize: 14, color: 'var(--muted)' }}>
              <span className="spin" />
              <span style={{ marginLeft: 10 }}>Loading the {shownProgram} matrix…</span>
            </div>
          ) : current.state === 'failed' ? (
            <div role="alert" style={{ padding: '28px 20px', fontSize: 14, color: 'var(--ink-2)' }}>
              {current.message}
            </div>
          ) : !doc ? (
            <div style={{ padding: '28px 20px', fontSize: 14, color: 'var(--ink-2)' }}>
              {NOTHING_HELD}
            </div>
          ) : image === 'failed' ? (
            <div role="alert" style={{ padding: '28px 20px', fontSize: 14, color: 'var(--ink-2)' }}>
              The lender&apos;s document isn&apos;t available right now, so page {page} cannot
              be shown. Try again in a moment, or ask your AE for the current matrix.
            </div>
          ) : (
            <div style={{ lineHeight: 0 }}>
              <img
                key={src}
                src={src!}
                alt={`Page ${page} of ${doc.title}`}
                onLoad={() => setLoadedSrc(src)}
                onError={() => setFailedSrc(src)}
                onClick={() => setZoom((z) => !z)}
                style={{
                  width: zoom ? '180%' : '100%', maxWidth: 'none', display: 'block',
                  cursor: zoom ? 'zoom-out' : 'zoom-in',
                  // Kept in the layout while it loads, so the box does not collapse and
                  // jump on every flip; the notice below says what is happening.
                  visibility: image === 'ready' ? 'visible' : 'hidden',
                  height: image === 'ready' ? 'auto' : 0,
                }}
              />
              {image === 'loading' ? (
                <div style={{ padding: '28px 20px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                  Loading page {page}…
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/*
          Two groups that wrap as groups: the pager stays one row at phone width, with the
          marker stacked under the page count so it does not push Next onto a line of its
          own, and the link and Close go underneath together.
        */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
          {doc ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" className="btn btn-ghost" style={PAGER_BUTTON}
                      disabled={page <= 1} onClick={() => goTo(page - 1)}>
                ← Previous
              </button>
              <span aria-live="polite" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'nowrap',
              }}>
                <span>Page {page} of {lastPage}</span>
                {isProgramPage ? (
                  <span className="pill" style={{
                    background: 'var(--blue-soft)', color: 'var(--blue)',
                    textTransform: 'none', letterSpacing: 0, padding: '1px 8px',
                  }}>
                    This program&apos;s grid
                  </span>
                ) : null}
              </span>
              <button type="button" className="btn btn-ghost" style={PAGER_BUTTON}
                      disabled={page >= lastPage} onClick={() => goTo(page + 1)}>
                Next →
              </button>
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 'auto' }}>
            {src ? (
              <a href={src} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                Open page in a new tab
              </a>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
